import type {
  CustomProduct,
  LandingPageBody,
  LandingPageParams,
} from "@apptus/esales-api";
import { Loop54Content } from "@server/utils/parseLoop/types";
import type { Variant } from "types";
import getRawLandingPage from "./getRawLandingPage";
import mapElevateFacets from "./mapElevateFacets";
import { mapElevateProduct } from "./mapElevateProducts";
import { mapElevateVariant } from "./mapElevateVariants";
import type { ElevateMarket } from "./utils";

export default async function getLandingPage(
  market: ElevateMarket,
  input: LandingPageParams,
  config?: LandingPageBody,
  allowDefault = true,
  explodeProducts = false,
): Promise<Loop54Content & { lists: Record<string, Variant[]> }> {
  const response = await getRawLandingPage(market, input, config);
  if (!response) {
    return {
      items: [],
      filter: [],
      count: 0,
      lists: {},
    };
  }
  const lists = Object.fromEntries(
    response.recommendationLists.map((list) => [
      list.id,
      list.productGroups.map((productGroup) =>
        mapElevateVariant({
          product: productGroup.products[0] as CustomProduct,
          market: market,
        }),
      ),
    ]),
  );

  if (!response.primaryList) {
    return {
      items: [],
      filter: [],
      count: 0,
      lists,
    };
  }

  const { productGroups, facets, totalHits: count } = response.primaryList;

  if (explodeProducts) {
    const items = productGroups.flatMap((productGroup) =>
      productGroup.products.map((product) =>
        mapElevateProduct({
          product,
          market: market,
        }),
      ),
    );
    return {
      items,
      filter: [],
      count: 0,
      lists: {},
    };
  }
  const items = productGroups.map((productGroup) =>
    mapElevateProduct({
      product: productGroup.products[0] as CustomProduct,
      market: market,
      config: {
        allowDefault,
        hasVariants: productGroup.products.length > 1,
        promoteProductImages: true,
      },
    }),
  );
  const filter = mapElevateFacets(facets);

  return { items, filter, count, lists };
}
